import type { ConfirmDialogOptions } from "@judo/feedback";
import { useModelLabel } from "@judo/i18n";
import type {
	Action,
	Button as ButtonElement,
	ButtonGroup as ButtonGroupElement,
	PageDefinition,
} from "@judo/model-api";
import { getButtonGroupTestId, getButtonTestId } from "@judo/test-ids";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Button, ButtonGroup, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { useMemo, useState } from "react";
import { getDestructiveConfirmationOptions, isDestructiveAction } from "../utils/destructive-action-utils";
import { findActionByDefinition } from "../utils/find-action-by-definition";
import { AUTO_FEATURED_ROW_ACTION_TYPES } from "../utils/table-column-utils";
import { IconRenderer } from "./IconRenderer";

export interface RowActionCellProps {
	/** Pre-filtered visible buttons for this row */
	visibleButtons: ButtonElement[];
	/** The ButtonGroup element from the model (for featuredActions, testId) */
	buttonGroupElement?: ButtonGroupElement;
	/** Page definition for resolving actions */
	page?: PageDefinition;
	/** Function to dispatch actions with row transfer context */
	dispatchRowAction: (action: Action, rowEntity: unknown) => Promise<void>;
	/** The transfer data for this row */
	rowEntity: unknown;
	/** Optional confirmation function for destructive actions */
	confirm?: (options: ConfirmDialogOptions) => Promise<boolean>;
}

/**
 * Checks whether a CRUD button should be visible for a given row transfer.
 * RowDeleteActionDefinition requires `__deleteable === true` on the row.
 * All other CRUD types are shown unconditionally.
 */
function isCrudButtonVisibleForRow(button: ButtonElement, rowEntity: unknown): boolean {
	const actionType = button.actionDefinition?.["@type"];
	if (actionType === "RowDeleteActionDefinition") {
		return (rowEntity as Record<string, unknown>)?.__deleteable === true;
	}
	return true;
}

/**
 * Renders row action buttons in a table cell with featured/overflow split.
 *
 * Uses the `featuredActions` property from the ButtonGroup model to determine
 * how many buttons are shown inline vs. in a dropdown menu:
 * - featuredActions = 0 (default): auto-CRUD mode — CRUD buttons shown inline,
 *   non-CRUD in dropdown. Delete requires `__deleteable === true` on the row.
 * - featuredActions > 0: first N as inline buttons, rest in dropdown
 * - featuredActions >= total: all buttons inline, no dropdown
 */
export function RowActionCell({
	visibleButtons,
	buttonGroupElement,
	page,
	dispatchRowAction,
	rowEntity,
	confirm,
}: RowActionCellProps) {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);

	const handleDropdownClick = (event: React.MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleButtonClick = async (event: React.MouseEvent, button: ButtonElement) => {
		event.stopPropagation();
		const actionType = button.actionDefinition?.["@type"];
		if (confirm && isDestructiveAction(actionType)) {
			const confirmed = await confirm(getDestructiveConfirmationOptions(actionType!));
			if (!confirmed) return;
		}
		const action = page ? findActionByDefinition(page, button.actionDefinition) : undefined;
		if (action) {
			dispatchRowAction(action as Action, rowEntity).catch((error) => {
				console.error("Error dispatching row action:", error);
			});
		}
	};

	const handleMenuItemClick = async (button: ButtonElement) => {
		handleClose();
		const actionType = button.actionDefinition?.["@type"];
		if (confirm && isDestructiveAction(actionType)) {
			const confirmed = await confirm(getDestructiveConfirmationOptions(actionType!));
			if (!confirmed) return;
		}
		const action = page ? findActionByDefinition(page, button.actionDefinition) : undefined;
		if (action) {
			dispatchRowAction(action as Action, rowEntity).catch((error) => {
				console.error("Error dispatching row action:", error);
			});
		}
	};

	if (visibleButtons.length === 0) {
		return null;
	}

	const featuredCount = buttonGroupElement?.featuredActions ?? 0;

	// Compute featured/overflow split
	// When featuredActions > 0: positional split (first N inline, rest overflow)
	// When featuredActions === 0: auto-CRUD mode (CRUD buttons inline, rest overflow)
	const { featuredButtons, overflowButtons } = useMemo(() => {
		if (featuredCount > 0) {
			return {
				featuredButtons: visibleButtons.slice(0, featuredCount),
				overflowButtons: visibleButtons.slice(featuredCount),
			};
		}

		// Auto-CRUD mode: CRUD buttons with passing guards go inline
		const featured: ButtonElement[] = [];
		const overflow: ButtonElement[] = [];

		for (const button of visibleButtons) {
			const actionType = button.actionDefinition?.["@type"];
			const isAutoFeatured = actionType != null && AUTO_FEATURED_ROW_ACTION_TYPES.has(actionType);

			if (isAutoFeatured) {
				// Auto-featured button: show inline only if per-row guard passes, otherwise hide entirely
				if (isCrudButtonVisibleForRow(button, rowEntity)) {
					featured.push(button);
				}
				// If guard fails (e.g., delete without __deleteable), button is excluded entirely
			} else {
				overflow.push(button);
			}
		}

		return { featuredButtons: featured, overflowButtons: overflow };
	}, [featuredCount, visibleButtons, rowEntity]);

	const groupTestId = buttonGroupElement ? getButtonGroupTestId(buttonGroupElement) : "row-actions";

	return (
		<>
			<ButtonGroup variant="outlined" size="small" sx={{ height: "100%", alignItems: "center" }}>
				{featuredButtons.map((button) => (
					<FeaturedActionButton key={button["xmi:id"]} button={button} onClick={(e) => handleButtonClick(e, button)} />
				))}
				{overflowButtons.length > 0 && (
					<Button
						size="small"
						onClick={handleDropdownClick}
						data-testid={`${groupTestId}::dropdown`}
						aria-controls={open ? `${groupTestId}-menu` : undefined}
						aria-haspopup="true"
						aria-expanded={open ? "true" : undefined}
						sx={{ minWidth: "auto", px: 1 }}
					>
						<MoreVertIcon sx={{ height: "1.3rem" }} />
					</Button>
				)}
			</ButtonGroup>
			{overflowButtons.length > 0 && (
				<Menu
					id={`${groupTestId}-menu`}
					anchorEl={anchorEl}
					open={open}
					onClose={handleClose}
					MenuListProps={{
						"aria-labelledby": `${groupTestId}::dropdown`,
					}}
				>
					{overflowButtons.map((button) => (
						<OverflowActionItem key={button["xmi:id"]} button={button} onClick={() => handleMenuItemClick(button)} />
					))}
				</Menu>
			)}
		</>
	);
}

/** Featured action button with translated label. */
function FeaturedActionButton({
	button,
	onClick,
}: {
	button: ButtonElement;
	onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) {
	const { label } = useModelLabel(button);
	const buttonTestId = getButtonTestId(button);
	return (
		<Button
			size="small"
			onClick={onClick}
			disabled={button.disabled}
			data-testid={buttonTestId}
			startIcon={button.icon ? <IconRenderer icon={button.icon} /> : undefined}
			sx={{ whiteSpace: "nowrap", minWidth: "auto" }}
		>
			{label}
		</Button>
	);
}

/** Overflow menu item with translated label. */
function OverflowActionItem({ button, onClick }: { button: ButtonElement; onClick: () => void }) {
	const { label } = useModelLabel(button);
	const buttonTestId = getButtonTestId(button);
	return (
		<MenuItem onClick={onClick} disabled={button.disabled} data-testid={buttonTestId}>
			{button.icon && (
				<ListItemIcon>
					<IconRenderer icon={button.icon} />
				</ListItemIcon>
			)}
			<ListItemText>{label}</ListItemText>
		</MenuItem>
	);
}
